import { useEffect, useRef, useState } from "react";

import { AnalyticsEvents, AnalyticsService } from "utils/analytics";

interface AudioLevelMonitorOptions {
  enabled?: boolean;
  onLevelChange?: (level: number) => void;
  checkForIssuesAfter?: number;
  onNoAudioDetected?: () => void;
  resetKey?: boolean;
}

/**
 * Hook to monitor audio levels using Web Audio API
 * Returns audio level on a 0-10 scale using RMS calculation
 * Based on Twilio's recommended approach from their best practices guide
 */
export const useAudioLevelMonitor = (
  mediaStreamTrack: MediaStreamTrack | null | undefined,
  options: AudioLevelMonitorOptions = {}
) => {
  const { enabled = true, onLevelChange, checkForIssuesAfter, onNoAudioDetected, resetKey } = options;

  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const hasDetectedAudioRef = useRef<boolean>(false);

  useEffect(() => {
    if (!mediaStreamTrack || !enabled) {
      return undefined;
    }

    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const samples = new Uint8Array(analyser.frequencyBinCount);

      const checkLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(samples);

        // Calculate RMS (root mean square)
        let sum = 0;
        for (let i = 0; i < samples.length; i += 1) {
          const sample = samples[i] / 255;
          sum += sample * sample;
        }
        const rms = Math.sqrt(sum / samples.length);

        // Convert to 0-10 scale
        const log2Rms = rms > 0 ? Math.log2(rms) : -Infinity;
        const level = log2Rms === -Infinity ? 0 : Math.min(10, Math.max(0, Math.ceil((10 * (log2Rms + 8)) / 8)));

        setAudioLevel(level);

        // Track if we've ever detected audio above 0
        if (level > 0) {
          hasDetectedAudioRef.current = true;
        }

        if (onLevelChange) {
          onLevelChange(level);
        }
      };

      const intervalId = window.setInterval(checkLevel, 100);
      intervalRef.current = intervalId;

      // Check for audio issues after specified time
      if (checkForIssuesAfter && onNoAudioDetected) {
        const timeoutId = window.setTimeout(() => {
          // Only trigger warning if we've NEVER detected any audio above 0
          if (!hasDetectedAudioRef.current) {
            onNoAudioDetected();
          }
        }, checkForIssuesAfter);
        timeoutRef.current = timeoutId;
      }
    } catch (error) {
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_MONITOR.INIT_ERROR, {
        error: String(error),
        trackId: mediaStreamTrack?.id,
      });
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      hasDetectedAudioRef.current = false;
      setAudioLevel(0);
    };
    // Re-run when track ID changes, enabled changes, or resetKey changes (for device switching)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaStreamTrack?.id, enabled, resetKey]);

  return audioLevel;
};
